import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import Backdrop from "./components/Backdrop";
import Cursor from "./components/Cursor";
import Footer from "./components/Footer";
import Navbar from "./components/Navbar";
import Home from "./pages/Home";
import Compare from "./pages/Compare";
import Injection from "./pages/Injection";
import { HealthProvider } from "./lib/health";
import { pageVariants, staticPageVariants } from "./lib/motion";
import "./styles/tokens.css";
import "./styles/base.css";
import "./styles/components.css";
import "./styles/viz.css";
import "./styles/pages.css";

function NotFound() {
  return (
    <div className="shell notfound">
      <span className="notfound__code">404</span>
      <h1 className="section-title">No such page</h1>
      <p className="section-lede" style={{ textAlign: "center" }}>
        That route does not exist. The two tools live at Compare and Injection.
      </p>
      <Link to="/" className="btn btn--ghost">
        <ArrowLeft size={15} aria-hidden="true" />
        Back to overview
      </Link>
    </div>
  );
}

function Shell() {
  const location = useLocation();
  const prefersReducedMotion = useReducedMotion();
  const mainRef = useRef(null);
  const firstRender = useRef(true);

  // On navigation, reset scroll and move focus into the new page so screen
  // reader and keyboard users land on the content, not back at the nav.
  //
  // A `#section` deep link is honoured instead, including on first load: the
  // browser's own anchor scroll runs before React has mounted the target, so
  // it has to be redone here once the section exists.
  useEffect(() => {
    const behavior = prefersReducedMotion ? "auto" : "smooth";
    const wasFirst = firstRender.current;
    firstRender.current = false;

    if (location.hash) {
      const frame = requestAnimationFrame(() => {
        const target = document.querySelector(location.hash);
        if (target) target.scrollIntoView({ behavior, block: "start" });
      });
      return () => cancelAnimationFrame(frame);
    }

    if (wasFirst) return undefined;
    window.scrollTo({ top: 0, behavior });
    mainRef.current?.focus({ preventScroll: true });
    return undefined;
  }, [location.pathname, location.hash, prefersReducedMotion]);

  const variants = prefersReducedMotion ? staticPageVariants : pageVariants;

  return (
    <>
      <a className="skip-link" href="#main">
        Skip to main content
      </a>
      <Backdrop />
      <Cursor />
      <Navbar />
      <main id="main" className="app-main" ref={mainRef} tabIndex={-1}>
        {/* mode="wait" keeps the outgoing page from overlapping the incoming
            one, so the shared background never shows two pages at once. */}
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={location.pathname}
            variants={variants}
            initial="initial"
            animate="enter"
            exit="exit"
          >
            <Routes location={location}>
              <Route path="/" element={<Home />} />
              <Route path="/compare" element={<Compare />} />
              <Route path="/injection" element={<Injection />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </motion.div>
        </AnimatePresence>
      </main>
      <Footer />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <HealthProvider>
        <Shell />
      </HealthProvider>
    </BrowserRouter>
  );
}

export default App;
